/**
 * Iter 102.4 · Batch 4 · Phase 3 — Cross-module Administrative Actions Log.
 *
 * Single auditable surface for every AAS write across modules. Route:
 *   Administration → Governance → Administrative Actions
 * Data source:
 *   GET /api/administrative-action/log?module=&severity=&subject=…
 *
 * Governed by:
 *   - EDDR-003 v2 (Service positioning — one log, many consumers)
 *   - Batch-4 directive ("do not create an attendance-specific log")
 *
 * The page intentionally does NOT surface implementation fields
 * (entity_id, actor_role, notification_state internals). What operators
 * see is the auditable "who/what/when/why/impact" story.
 */
import { useCallback, useEffect, useMemo, useState, Fragment } from "react";
import { FunnelSimple, Gavel, ArrowClockwise, MagnifyingGlass } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import PageHeader from "../../components/layout/PageHeader";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { formatDate } from "../../lib/format";

const SEVERITY_STYLE = {
    low: "bg-slate-100 text-slate-700 border-slate-300",
    medium: "bg-amber-100 text-amber-800 border-amber-300",
    high: "bg-orange-100 text-orange-800 border-orange-300",
    critical: "bg-rose-100 text-rose-800 border-rose-300",
};

const APPROVAL_STYLE = {
    n_a: "bg-slate-100 text-slate-500",
    pending: "bg-blue-100 text-blue-800",
    approved: "bg-emerald-100 text-emerald-800",
    rejected: "bg-rose-100 text-rose-800",
};

const IMPACT_SHORT = {
    impact_attendance: "Attendance",
    impact_leave_ledger: "Leave",
    impact_payroll: "Payroll",
    impact_compliance: "Compliance",
    impact_analytics: "Analytics",
    impact_employee_timeline: "Timeline",
    impact_reversible: "Reversible",
};

export default function AdministrativeActionsLog() {
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState({
        module: "", severity: "", action_type: "",
        subject_user_id: "", actor_user_id: "",
        effective_from_date: "", effective_to_date: "",
        approval_state: "", is_reversed: "",
    });
    const [offset, setOffset] = useState(0);
    const [expanded, setExpanded] = useState(null);
    const LIMIT = 50;

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const params = { limit: LIMIT, offset };
            for (const [k, v] of Object.entries(filters)) {
                if (v === "" || v === null || v === undefined) continue;
                if (k === "is_reversed") params[k] = v === "true";
                else params[k] = v;
            }
            const { data } = await api.get("/administrative-action/log", { params });
            setRows(data.rows || []);
            setTotal(data.total || 0);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load administrative actions"));
        } finally {
            setLoading(false);
        }
    }, [filters, offset]);

    useEffect(() => { reload(); }, [reload]);

    const resetFilters = () => {
        setFilters({
            module: "", severity: "", action_type: "",
            subject_user_id: "", actor_user_id: "",
            effective_from_date: "", effective_to_date: "",
            approval_state: "", is_reversed: "",
        });
        setOffset(0);
    };

    const modules = useMemo(
        () => Array.from(new Set(rows.map((r) => r.module))).sort(),
        [rows],
    );
    const actionTypes = useMemo(
        () => Array.from(new Set(rows.map((r) => r.action_type))).sort(),
        [rows],
    );

    return (
        <div className="px-6 sm:px-8 py-6 max-w-[1400px]" data-testid="aa-log-page">
            <PageHeader
                eyebrow="Administration · Governance"
                title="Administrative Actions"
                description="Cross-module audit surface for every administrative override — attendance today, leave / payroll / claims / assets as they adopt the Service. Every row is an immutable, actor-attributed, reason-mandatory write."
                actions={
                    <div className="flex gap-2">
                        <Button variant="outline" onClick={resetFilters} data-testid="aa-log-reset">
                            <ArrowClockwise size={14} className="mr-1" /> Reset
                        </Button>
                    </div>
                }
            />

            {/* Filters */}
            <div
                className="mt-5 bg-white rounded-xl border border-slate-200 p-4 grid grid-cols-2 md:grid-cols-4 gap-3"
                data-testid="aa-log-filters"
            >
                <div>
                    <Label className="text-xs">Module</Label>
                    <select
                        className="h-10 w-full rounded-md border border-slate-300 px-3 text-sm bg-white"
                        value={filters.module}
                        onChange={(e) => { setFilters({ ...filters, module: e.target.value }); setOffset(0); }}
                        data-testid="aa-log-filter-module"
                    >
                        <option value="">All modules</option>
                        <option value="attendance">Attendance</option>
                        {modules.filter((m) => m !== "attendance").map((m) => (
                            <option key={m} value={m}>{m}</option>
                        ))}
                    </select>
                </div>
                <div>
                    <Label className="text-xs">Severity</Label>
                    <select
                        className="h-10 w-full rounded-md border border-slate-300 px-3 text-sm bg-white"
                        value={filters.severity}
                        onChange={(e) => { setFilters({ ...filters, severity: e.target.value }); setOffset(0); }}
                        data-testid="aa-log-filter-severity"
                    >
                        <option value="">Any</option>
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                        <option value="critical">Critical</option>
                    </select>
                </div>
                <div>
                    <Label className="text-xs">Action Type</Label>
                    <select
                        className="h-10 w-full rounded-md border border-slate-300 px-3 text-sm bg-white"
                        value={filters.action_type}
                        onChange={(e) => { setFilters({ ...filters, action_type: e.target.value }); setOffset(0); }}
                        data-testid="aa-log-filter-action-type"
                    >
                        <option value="">Any</option>
                        {actionTypes.map((t) => (
                            <option key={t} value={t}>{t}</option>
                        ))}
                    </select>
                </div>
                <div>
                    <Label className="text-xs">Approval State</Label>
                    <select
                        className="h-10 w-full rounded-md border border-slate-300 px-3 text-sm bg-white"
                        value={filters.approval_state}
                        onChange={(e) => { setFilters({ ...filters, approval_state: e.target.value }); setOffset(0); }}
                        data-testid="aa-log-filter-approval"
                    >
                        <option value="">Any</option>
                        <option value="n_a">Not required</option>
                        <option value="pending">Pending</option>
                        <option value="approved">Approved</option>
                        <option value="rejected">Rejected</option>
                    </select>
                </div>
                <div>
                    <Label className="text-xs">Subject User ID</Label>
                    <Input
                        value={filters.subject_user_id}
                        onChange={(e) => { setFilters({ ...filters, subject_user_id: e.target.value }); setOffset(0); }}
                        placeholder="USR-…"
                        data-testid="aa-log-filter-subject"
                    />
                </div>
                <div>
                    <Label className="text-xs">Actor User ID</Label>
                    <Input
                        value={filters.actor_user_id}
                        onChange={(e) => { setFilters({ ...filters, actor_user_id: e.target.value }); setOffset(0); }}
                        placeholder="USR-…"
                        data-testid="aa-log-filter-actor"
                    />
                </div>
                <div>
                    <Label className="text-xs">Effective From</Label>
                    <Input
                        type="date"
                        value={filters.effective_from_date}
                        onChange={(e) => { setFilters({ ...filters, effective_from_date: e.target.value }); setOffset(0); }}
                        data-testid="aa-log-filter-effective-from"
                    />
                </div>
                <div>
                    <Label className="text-xs">Effective To</Label>
                    <Input
                        type="date"
                        value={filters.effective_to_date}
                        onChange={(e) => { setFilters({ ...filters, effective_to_date: e.target.value }); setOffset(0); }}
                        data-testid="aa-log-filter-effective-to"
                    />
                </div>
            </div>

            {/* Table */}
            <section className="mt-5 bg-white rounded-xl border border-slate-200 p-5" data-testid="aa-log-section">
                <div className="flex justify-between items-center mb-3">
                    <div className="text-xs text-slate-500 flex items-center gap-3">
                        <span>
                            <FunnelSimple size={12} className="inline mr-1" />
                            {loading ? "Loading…" : `${total} action(s)`}
                        </span>
                        <span className="text-[10px] uppercase tracking-wider text-slate-400" data-testid="aa-log-sort-hint">
                            Sorted · newest first
                        </span>
                    </div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-400">
                        All times shown in server-local (UTC).
                    </div>
                </div>
                <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-slate-600 text-xs">
                        <tr>
                            <th className="text-left px-3 py-2 font-medium">When</th>
                            <th className="text-left px-3 py-2 font-medium">Module · Action</th>
                            <th className="text-left px-3 py-2 font-medium">Severity</th>
                            <th className="text-left px-3 py-2 font-medium">Subject / Effective</th>
                            <th className="text-left px-3 py-2 font-medium">Actor</th>
                            <th className="text-left px-3 py-2 font-medium">Impact</th>
                            <th className="text-left px-3 py-2 font-medium">Approval</th>
                            <th className="text-left px-3 py-2 font-medium"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 && (
                            <tr>
                                <td colSpan={8} className="py-10 text-center text-slate-400">
                                    <Gavel size={20} className="mx-auto mb-2 text-slate-300" />
                                    No administrative actions match the current filters.
                                </td>
                            </tr>
                        )}
                        {rows.map((r) => {
                            const isExpanded = expanded === r.id;
                            return (
                                <Fragment key={r.id}>
                                    <tr
                                        className="border-t border-slate-100 hover:bg-slate-50/50 cursor-pointer"
                                        onClick={() => setExpanded(isExpanded ? null : r.id)}
                                        data-testid={`aa-log-row-${r.id}`}
                                    >
                                        <td className="px-3 py-2 text-xs text-slate-500 whitespace-nowrap">
                                            {formatDate(r.action_timestamp) || r.action_timestamp?.slice(0, 10)}
                                            <div className="text-[10px] font-mono text-slate-400">
                                                {r.action_timestamp?.slice(11, 19)}
                                            </div>
                                        </td>
                                        <td className="px-3 py-2 text-xs">
                                            <div className="font-medium text-slate-800 capitalize">
                                                {r.module}
                                            </div>
                                            <div className="text-slate-500">{r.action_type}</div>
                                        </td>
                                        <td className="px-3 py-2 text-xs">
                                            <Badge className={`border ${SEVERITY_STYLE[r.severity]} text-[10px] font-semibold`}>
                                                {r.severity?.toUpperCase()}
                                            </Badge>
                                        </td>
                                        <td className="px-3 py-2 text-xs">
                                            <div className="font-mono text-[11px] text-slate-700">{r.subject_user_id}</div>
                                            <div className="text-[10px] text-slate-400">
                                                effective {r.effective_date}
                                            </div>
                                        </td>
                                        <td className="px-3 py-2 text-xs">
                                            <div className="text-slate-700">{r.actor_email}</div>
                                        </td>
                                        <td className="px-3 py-2 text-xs">
                                            <div className="flex flex-wrap gap-1">
                                                {(r.impact_flags || []).map((f) => (
                                                    <Badge
                                                        key={f}
                                                        variant="outline"
                                                        className="text-[10px] bg-white border-slate-300"
                                                    >
                                                        {IMPACT_SHORT[f] || f}
                                                    </Badge>
                                                ))}
                                            </div>
                                        </td>
                                        <td className="px-3 py-2 text-xs">
                                            <Badge className={`${APPROVAL_STYLE[r.approval_state] || "bg-slate-100 text-slate-500"} text-[10px]`}>
                                                {r.approval_state}
                                            </Badge>
                                            {r.reversal_action_id && (
                                                <div className="text-[10px] text-rose-600 mt-1">reversed</div>
                                            )}
                                            {r.is_reversal_of && (
                                                <div className="text-[10px] text-violet-600 mt-1">reversal-of prior</div>
                                            )}
                                        </td>
                                        <td className="px-3 py-2 text-xs text-slate-400">
                                            <MagnifyingGlass size={14} />
                                        </td>
                                    </tr>
                                    {isExpanded && (
                                        <tr className="bg-slate-50/50 border-t border-slate-100" data-testid={`aa-log-expanded-${r.id}`}>
                                            <td colSpan={8} className="px-6 py-4">
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-xs">
                                                    <div>
                                                        <div className="text-slate-500 mb-0.5">Reason</div>
                                                        <div className="text-slate-800 italic">&ldquo;{r.reason}&rdquo;</div>
                                                    </div>
                                                    <div>
                                                        <div className="text-slate-500 mb-0.5">Entity</div>
                                                        <div className="text-slate-700 font-mono text-[11px]">
                                                            {r.entity_type} · {r.entity_composite_key && JSON.stringify(r.entity_composite_key)}
                                                        </div>
                                                    </div>
                                                    {r.old_state && Object.keys(r.old_state).length > 0 && (
                                                        <div>
                                                            <div className="text-slate-500 mb-0.5">Prior State</div>
                                                            <pre className="text-slate-700 bg-white border border-slate-200 rounded p-2 text-[10px] overflow-x-auto">
                                                                {JSON.stringify(r.old_state, null, 2)}
                                                            </pre>
                                                        </div>
                                                    )}
                                                    {r.new_state && Object.keys(r.new_state).length > 0 && (
                                                        <div>
                                                            <div className="text-slate-500 mb-0.5">New State</div>
                                                            <pre className="text-slate-700 bg-white border border-slate-200 rounded p-2 text-[10px] overflow-x-auto">
                                                                {JSON.stringify(r.new_state, null, 2)}
                                                            </pre>
                                                        </div>
                                                    )}
                                                    {r.approvers && r.approvers.length > 0 && (
                                                        <div className="md:col-span-2">
                                                            <div className="text-slate-500 mb-0.5">Approval chain</div>
                                                            <div className="flex flex-wrap gap-2">
                                                                {r.approvers.map((a, i) => (
                                                                    <Badge
                                                                        key={i}
                                                                        className={`text-[10px] ${a.decision === "approved" ? "bg-emerald-100 text-emerald-800" : a.decision === "rejected" ? "bg-rose-100 text-rose-800" : "bg-blue-100 text-blue-800"}`}
                                                                    >
                                                                        {a.role} · {a.decision || "pending"}
                                                                        {a.decided_by_email && ` · ${a.decided_by_email}`}
                                                                    </Badge>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}
                                                    {(r.reversal_action_id || r.is_reversal_of) && (
                                                        <div className="md:col-span-2 text-[11px]">
                                                            {r.reversal_action_id && (
                                                                <div className="text-rose-700">
                                                                    Reversed by action <span className="font-mono">{r.reversal_action_id}</span>
                                                                </div>
                                                            )}
                                                            {r.is_reversal_of && (
                                                                <div className="text-violet-700">
                                                                    Reverses prior action <span className="font-mono">{r.is_reversal_of}</span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </Fragment>
                            );
                        })}
                    </tbody>
                </table>

                {total > LIMIT && (
                    <div className="mt-3 flex justify-end gap-2 text-xs items-center">
                        <Button
                            variant="outline"
                            size="sm"
                            disabled={offset === 0}
                            onClick={() => setOffset((o) => Math.max(0, o - LIMIT))}
                            data-testid="aa-log-prev"
                        >
                            Prev
                        </Button>
                        <span>
                            {offset + 1} – {Math.min(offset + LIMIT, total)} of {total}
                        </span>
                        <Button
                            variant="outline"
                            size="sm"
                            disabled={offset + LIMIT >= total}
                            onClick={() => setOffset((o) => o + LIMIT)}
                            data-testid="aa-log-next"
                        >
                            Next
                        </Button>
                    </div>
                )}
            </section>
        </div>
    );
}
